import { ActionReducerMap, createReducer, on } from '@ngrx/store';

import { SwitchAttrsDomain } from '@mode/shared/contract-common';
import { SwitchAttrsActions, SwitchViewActions } from '@mode/switch-views/contract';
import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { DatasetActions } from '@mode/dataset/contract';

export type SwitchAttrsState = EntityState<SwitchAttrsDomain.SwitchAttrs>;

export interface SwitchAttrsFeatureState {
  attrs: SwitchAttrsState;
}

export const switchAttrsFeatureKey = 'switchAttrs';

const adapter: EntityAdapter<SwitchAttrsDomain.SwitchAttrs> = createEntityAdapter<SwitchAttrsDomain.SwitchAttrs>({
  selectId: (model) => model.token,
});

export const initialState: SwitchAttrsFeatureState = {
  attrs: adapter.getInitialState(),
};

export const attrsReducer = createReducer(
  initialState.attrs,
  on(SwitchViewActions.loadSwitchViewSuccess, DatasetActions.loadDatasetSuccess, (state, action) => {
    const switchView = action.switchView;
    const switchAttrs = {
      token: switchView.token,
      switchAttrsData: switchView.switchAttrs,
    };
    return adapter.setOne(switchAttrs, state);
  }),
  on(SwitchViewActions.pollingSwitchViewCompleted, (state, action) => {
    const switchView = action.dataView.switchView;
    const switchAttrs = {
      token: switchView.token,
      switchAttrsData: switchView.switchAttrs,
    };
    return adapter.setOne(switchAttrs, state);
  }),
  on(
    SwitchViewActions.loadGfDataViews,
    SwitchViewActions.loadEditorViewsSuccess,
    SwitchViewActions.refreshEditorViewsSuccess,
    SwitchViewActions.loadReportViewDataViews,
    (state, action) => {
      const switchAttrs = action.dataViews.map(({ switchView }) => ({
        token: switchView.token,
        switchAttrsData: switchView.switchAttrs,
      }));

      /**
       * @todo(@kevinjamesus86) GF
       * @description We are clearing out the entity store with `setAll` to resolve an
       * ambiguity issue in looking up switch views by queryToken. The following flow
       * exercises the issue:
       *
       * [Load] View Report
       *   ** action:loadGfDataViews
       *   -> Click [Explore] on a viz
       *   -> Save an exploration of the viz
       *   ** action:loadExploration
       *   -> [★★ see below ★★]
       *
       * Problem
       * -------
       *
       * There are now 2 SwitchViews in the store that ref the same queryToken. This presents
       * an issue downstream with `getModel` selectors and the way we look up views in a model
       * by viewName, which is a query token. There will be two views in the model that are keyed by
       * said query token; the first match in the collection is returned by Switch.Views.find(model, viewName).
       * As it is right now, we do not have a way to distinguish between two views in a model or
       * select a _specific_ one in the case more than one matches a query token.
       *
       * [★★] Problem in practice
       * [★★] -------------------
       *
       * After saving the exploration, _without reloading the page_, create a calculated field
       * that's local to the exploration. The new calulation will be added to the correct SwitchView
       * and show up in the field list as expected; but, adding said field to the encoding will result
       * in a broken viz / _invalid encoding_. This is because the encoding builder selects a view from
       * the model by query token, vs. working with the view$ observable passed down from the observables
       * factory in a parent component.. which selects and constructs the correct view [by SwitchView token].
       *
       * Round up
       * --------
       *
       * Reports | Explorations load the data they need when they need it; clearing out the store
       * per action should not adversely affect the other. This works for now 👍
       *
       *             ↓↓↓↓↓↓ setAll replaces the existing collection with the provided collection
       */
      return adapter.setAll(switchAttrs, state);
    }
  ),

  on(SwitchViewActions.loadExplorationDataView, (state, { dataView }) => {
    const switchView = dataView.switchView;
    const switchAttrs = {
      token: switchView.token,
      switchAttrsData: switchView.switchAttrs,
    };
    return adapter.setOne(
      switchAttrs,
      /**
       * @todo(@kevinjamesus86) GF
       * @description see above comments for on(loadGfDataViews, ...)
       */
      adapter.removeAll(state)
    );
  }),

  on(SwitchViewActions.loadVisualizationDataView, (state, action) => {
    const switchView = action.dataView.switchView;
    const switchAttrs = {
      token: switchView.token,
      switchAttrsData: switchView.switchAttrs,
    };
    return adapter.setOne(switchAttrs, state);
  }),
  on(SwitchViewActions.loadImportedDatasetSuccess, (state, action) => {
    const switchView = action.dataView.switchView;
    const switchAttrs = {
      token: switchView.token,
      switchAttrsData: switchView.switchAttrs,
    };
    return adapter.addOne(switchAttrs, state);
  }),

  on(SwitchViewActions.importedDatasetRefreshed, (state, action) => {
    const switchView = action.switchView;
    const switchAttrs = {
      token: switchView.token,
      switchAttrsData: switchView.switchAttrs,
    };
    return adapter.setOne(switchAttrs, state);
  }),
  on(
    SwitchAttrsActions.createSwitchViewAttr,
    SwitchAttrsActions.createSwitchViewAttrFailure,
    SwitchAttrsActions.createSwitchViewAttrFromDataview,
    (state) => state
  ),
  on(
    SwitchAttrsActions.createSwitchViewAttrSuccess,
    SwitchAttrsActions.createSwitchViewAttrFromDataViewSuccess,
    (state, { token, attr }) => {
      const switchAttrs = state.entities[token];
      attr.isLocal = true;
      switchAttrs?.switchAttrsData.push(attr);
      return adapter.updateOne(
        {
          id: token,
          changes: {
            switchAttrsData: switchAttrs?.switchAttrsData,
          },
        },
        state
      );
    }
  ),

  on(SwitchAttrsActions.deleteSwitchViewAttr, (state) => state),
  on(SwitchAttrsActions.deleteSwitchViewAttrFailure, (state) => state),
  on(SwitchAttrsActions.deleteSwitchViewAttrSuccess, (state, { token, attr }) => {
    const switchAttrsData = state.entities[token]?.switchAttrsData as SwitchAttrsDomain.SwitchAttr[];
    const switchAttrIndex = switchAttrsData.findIndex((attrs) => attrs.token === attr.token);
    switchAttrsData.splice(switchAttrIndex, 1);
    return adapter.updateOne(
      {
        id: token,
        changes: {
          switchAttrsData,
        },
      },
      state
    );
  }),

  on(
    SwitchAttrsActions.updateSwitchViewAttrSuccess,
    SwitchAttrsActions.updateSwitchViewAttrFromDataViewSuccess,
    (state, { token, attr }) => {
      const switchAttrsData = state.entities[token]?.switchAttrsData as SwitchAttrsDomain.SwitchAttr[];
      const switchAttrIndex = switchAttrsData.findIndex((attrs) => attrs.token === attr.token);
      attr.isLocal = true;
      switchAttrsData.splice(switchAttrIndex, 1, attr);

      return adapter.updateOne(
        {
          id: token,
          changes: {
            switchAttrsData: switchAttrsData,
          },
        },
        state
      );
    }
  ),

  /**
   * @todo(@kevinjamesus86)
   * @description While exploring a viz or exploration we need to be able to make
   * changes to a SwitchView's attrs without mucking up the parent attrs. When entering
   * an explor*ing* state, we preserve a copy of the original switchAttrs on the entity
   * which allows us to restore the original attrs when exiting the explor*ing* state.
   * Before restoring the original attrs, we can select the _local_ attrs from the explor*ing*
   * state to save along with the exploraton, if one is saved.
   *
   * ** These properties are specifically _not_ provided in the interface because
   * ** we want this to be a reducer-only concern; ie. we don't want this information
   * ** propagating throughout the app. This is effectively a private API that works
   * ** through the issue above, and is an extension of the problem solving done for
   * ** [see comment for on(loadGfDataViews) reducer]
   */
  on(SwitchAttrsActions.makeOriginalAttrs, (state) => {
    return adapter.map((entity) => {
      if (!entity.originalAttrs) {
        const originalAttrs = entity.switchAttrsData;
        const switchAttrs = originalAttrs.map((attr) => ({ ...attr, isLocal: attr.isLocal === true }));
        return {
          ...entity,
          switchAttrs,
          originalAttrs,
        };
      }
      return entity;
    }, state);
  }),

  on(SwitchAttrsActions.restoreOriginalAttrs, (state) => {
    for (const entity of selectAll(state)) {
      if (entity.originalAttrs) {
        const { originalAttrs: switchAttrsData, ...rest } = entity;
        state = adapter.setOne(
          {
            ...rest,
            switchAttrsData,
          },
          state
        );
      }
    }
    return state;
  })
);

const { selectAll, selectIds, selectEntities } = adapter.getSelectors();

const reducer: ActionReducerMap<SwitchAttrsFeatureState> = {
  attrs: attrsReducer,
};

export const switchAttrsAdapter = adapter;

export { adapter, reducer };

export const selectAllAttrs = selectAll;
export const selectAttrsTokens = selectIds;
export const selectAttrsEntities = selectEntities;
